import type { ManagerPattern } from '../types/patterns';
import { PatternCaseCard } from './PatternCaseCard';

interface PatternStageProps {
  pattern: ManagerPattern;
  /** Opens the full examine view for a case's underlying break. */
  onDeepDive: (breakId: string) => void;
}

const URGENCY_TEXT: Record<ManagerPattern['severity'], string> = {
  high: 'Critical',
  medium: 'Needs attention',
  low: 'Watch',
};

/** Saturated left rail - the loudest, cleanest "this is critical" signal. */
const STAGE_RAIL: Record<ManagerPattern['severity'], string> = {
  high: 'bg-rose-500',
  medium: 'bg-amber-400',
  low: 'bg-zinc-200',
};

/** Solid, filled urgency pill - reads as an alert, not a label. */
const STAGE_PILL: Record<ManagerPattern['severity'], string> = {
  high: 'bg-rose-600 text-white shadow-[0_6px_18px_-4px_rgba(244,63,94,0.55)]',
  medium: 'bg-amber-500 text-white',
  low: 'bg-zinc-100 text-zinc-600 ring-1 ring-inset ring-zinc-200',
};

export function PatternStage({ pattern, onDeepDive }: PatternStageProps) {
  const openCasesLabel = `${pattern.cases.length} open case${
    pattern.cases.length === 1 ? '' : 's'
  }`;

  return (
    <div className="relative overflow-hidden rounded-3xl border border-zinc-100 bg-white p-7 shadow-[0_40px_90px_-45px_rgba(16,27,58,0.55)] sm:p-9 md:p-10">
      {/* Severity rail - a saturated edge that brands the whole card critical. */}
      <span
        aria-hidden="true"
        className={`pointer-events-none absolute inset-y-6 start-0 w-1.5 rounded-full ${
          STAGE_RAIL[pattern.severity]
        }`}
      />

      {/* Header */}
      <div className="relative max-w-3xl">
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${
              STAGE_PILL[pattern.severity]
            }`}
          >
            {pattern.severity === 'high' && (
              <svg
                viewBox="0 0 24 24"
                className="h-3 w-3"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M13 2L3 14h7l-1 8 10-12h-7l1-8z" />
              </svg>
            )}
            {URGENCY_TEXT[pattern.severity]}
          </span>
          {pattern.isRecurring && (
            <span className="inline-flex items-center rounded-full border border-zinc-200 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-500">
              Recurring
            </span>
          )}
        </div>

        <h2 className="text-2xl font-light leading-tight tracking-tight text-black sm:text-3xl md:text-[2.1rem]">
          {pattern.title}
        </h2>

        <p className="mt-4 text-sm leading-relaxed text-zinc-600">
          <span className="me-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
            Playbook
          </span>
          {pattern.playbookRule}
        </p>
      </div>

      {/* Impact strip */}
      <div className="relative mt-8 grid grid-cols-2 gap-y-6 border-y border-zinc-100 py-6 md:flex md:items-end md:gap-0">
        {pattern.metrics.map((metric, metricIndex) => (
          <div
            key={metric.label}
            className={`px-1 md:flex-1 md:px-6 ${
              metricIndex > 0 ? 'md:border-s md:border-zinc-100' : 'md:ps-0'
            }`}
          >
            <div className="flex items-baseline gap-1.5">
              <span
                className={`text-4xl font-normal tracking-tight md:text-5xl ${
                  metric.isUrgent ? 'text-rose-600' : 'text-zinc-900'
                }`}
              >
                {metric.value}
              </span>
              {metric.unit && (
                <span
                  className={`text-sm font-normal ${
                    metric.isUrgent ? 'text-rose-500' : 'text-zinc-400'
                  }`}
                >
                  {metric.unit}
                </span>
              )}
            </div>
            <p className="mt-1.5 text-xs text-zinc-600">{metric.label}</p>
          </div>
        ))}
      </div>

      {/* Cases */}
      <p className="mt-7 mb-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-zinc-500">
        Where it&apos;s breaking — {openCasesLabel}
      </p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {pattern.cases.map((patternCase) => (
          <PatternCaseCard
            key={patternCase.id}
            patternCase={patternCase}
            onDeepDive={onDeepDive}
          />
        ))}
      </div>

      {/* Intera suggests - a structural fix for recurrence the manager keeps
          absorbing by hand. Brand cyan reads as constructive, not an alarm. */}
      {pattern.recommendation && (
        <div className="relative mt-8 overflow-hidden rounded-2xl border border-[#00AEEF]/30 bg-gradient-to-br from-[#00AEEF]/[0.07] via-white to-[#22D1C5]/[0.06] p-6 sm:p-7">
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-0 start-0 w-1 bg-gradient-to-b from-[#1678C2] to-[#22D1C5]"
          />
          <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#0F2F5F]">
                <svg
                  viewBox="0 0 24 24"
                  className="h-3.5 w-3.5 text-[#00AEEF]"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d="M12 2a7 7 0 0 0-4 12.74V17a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-2.26A7 7 0 0 0 12 2zM9 21a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1v-1H9v1z" />
                </svg>
                {pattern.recommendation.eyebrow}
              </span>
              <h3 className="mt-2 text-lg font-semibold tracking-tight text-black sm:text-xl">
                {pattern.recommendation.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-600">
                {pattern.recommendation.rationale}
              </p>
              {pattern.recommendation.proofPoints && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {pattern.recommendation.proofPoints.map((proofPoint) => (
                    <span
                      key={proofPoint}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#00AEEF]/20 bg-white/70 px-3 py-1 text-[11px] font-medium text-[#0F2F5F]"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-[#00AEEF]" />
                      {proofPoint}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-gradient-to-r from-[#0F2F5F] via-[#1678C2] to-[#22D1C5] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(22,120,194,0.6)] transition-transform hover:scale-[1.02] active:scale-95"
            >
              {pattern.recommendation.ctaLabel}
              <svg
                viewBox="0 0 24 24"
                className="h-4 w-4 rtl:rotate-180"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Footer - one clear primary action so the manager knows to act. */}
      <div className="mt-8 flex items-center justify-between gap-4 border-t border-zinc-100 pt-6">
        <button
          type="button"
          className="text-sm font-medium text-zinc-400 transition-colors hover:text-zinc-600"
        >
          Snooze pattern
        </button>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-[#0F2F5F] via-[#1678C2] to-[#22D1C5] px-5 py-2.5 text-sm font-semibold text-white shadow-[0_12px_30px_-10px_rgba(22,120,194,0.6)] transition-transform hover:scale-[1.02] active:scale-95"
        >
          Resolve pattern
          <svg
            viewBox="0 0 24 24"
            className="h-4 w-4 rtl:rotate-180"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  );
}
